import { useTranslation } from "react-i18next";

import { AvailabilityGate } from "@shared/components/AvailabilityGate/AvailabilityGate";
import { useSelfCheckoutFlow } from "@shared/selfCheckoutFlow/useSelfCheckoutFlow";
import { useGlobalConfig } from "@unauthenticated/shared/context/GlobalConfig/GlobalConfig";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

interface Props {
  initialEmail?: string;
  initialZipCode?: string;
  loading?: boolean;
  /** Display name of the lab test/panel, when the item resolved. */
  itemName?: string | null;
  onContinue: (email: string, zipCode: string) => void;
}

/**
 * Direct-lab entry availability step — thin wrapper over the presentational
 * AvailabilityGate. Collects email + zip (fields from the directLab flow
 * descriptor); the page runs the zip-derived state-restriction gate before
 * starting auth (BUT-1184).
 */
const DirectLabsAuthStartForm = ({
  initialEmail = "",
  initialZipCode = "",
  loading = false,
  itemName,
  onContinue,
}: Props) => {
  const { t } = useTranslation();
  const { emailRegex } = useGlobalConfig();
  const flow = useSelfCheckoutFlow("directLab");

  const title = itemName
    ? t(l.directLabsCheckout.authStart.title, { itemName })
    : t(l.directLabsCheckout.authStart.titleFallback);

  return (
    <AvailabilityGate
      fields={flow.entryFields}
      copy={{
        title,
        description: t(l.directLabsCheckout.authStart.description),
        emailLabel: t(l.directLabsCheckout.authStart.emailLabel),
        zipCodeLabel: t(l.directLabsCheckout.authStart.zipCodeLabel),
        zipInvalidError: t(l.directLabsCheckout.authStart.errors.zipInvalid),
        locationNote: t(l.directLabsCheckout.authStart.locationNote),
        continueLabel: t(l.directLabsCheckout.authStart.continueButton),
        emailInvalidError: t(l.directLabsCheckout.authStart.errors.emailInvalid),
      }}
      onContinue={({ email, zipCode }) => onContinue(email, zipCode || "")}
      loading={loading}
      initialEmail={initialEmail}
      initialZipCode={initialZipCode}
      emailRegex={emailRegex}
    />
  );
};

export { DirectLabsAuthStartForm };
